import React, { useState } from 'react';

interface CollapsibleSectionProps {
  title: string;
  children: React.ReactNode;
  defaultExpanded?: boolean;
  variant?: 'default' | 'danger' | 'warning' | 'success' | 'info';
  className?: string;
  icon?: string;
}

const CollapsibleSection: React.FC<CollapsibleSectionProps> = ({ 
  title, 
  children, 
  defaultExpanded = false, 
  variant = 'default',
  className = '',
  icon
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const getVariantStyles = () => {
    switch (variant) {
      case 'danger':
        return {
          header: '#dc2626',
          headerHover: '#b91c1c',
          background: '#1f2937',
          border: '#dc2626'
        };
      case 'warning':
        return {
          header: '#d97706',
          headerHover: '#b45309',
          background: '#1f2937',
          border: '#d97706'
        };
      case 'success':
        return {
          header: '#D4A017',
          headerHover: '#B8860B',
          background: '#1f2937',
          border: '#D4A017'
        };
      case 'info':
        return {
          header: '#0891b2',
          headerHover: '#0e7490',
          background: '#1f2937',
          border: '#0891b2'
        };
      default:
        return {
          header: '#374151',
          headerHover: '#4b5563',
          background: '#1f2937',
          border: '#4b5563'
        };
    }
  };

  const styles = getVariantStyles();

  return (
    <div 
      className={`collapsible-section ${className}`}
      style={{
        background: styles.background,
        borderRadius: '12px',
        border: `1px solid ${styles.border}`,
        marginBottom: '1rem',
        overflow: 'hidden',
        transition: 'all 0.2s ease'
      }}
    >
      <button
        type="button"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          width: '100%',
          padding: '1rem 1.5rem',
          background: styles.header,
          border: 'none',
          color: '#f9fafb',
          fontSize: '1.125rem',
          fontWeight: 'bold',
          textAlign: 'left',
          cursor: 'pointer',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => {
          e.currentTarget.style.background = styles.headerHover;
        }}
        onMouseOut={(e) => {
          e.currentTarget.style.background = styles.header;
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          {icon && <span style={{ fontSize: '1.25rem' }}>{icon}</span>}
          <span>{title}</span>
        </div>
        <span style={{
          transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'transform 0.2s ease',
          fontSize: '1rem'
        }}>
          ▼
        </span>
      </button>
      
      <div style={{
        maxHeight: isExpanded ? '2000px' : '0',
        overflow: 'hidden',
        transition: 'max-height 0.3s ease-in-out'
      }}>
        <div style={{ 
          padding: isExpanded ? '1.5rem' : '0 1.5rem',
          transition: 'padding 0.3s ease-in-out'
        }}>
          {children}
        </div>
      </div>
    </div>
  );
};

export default CollapsibleSection;